import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardListIcon, InfoIcon } from 'lucide-react';
interface MerchantRequest {
  id: string;
  type: string;
  description: string;
  requestedBy: string;
  requestedAt: string;
  status: string;
  details: {
    paymentMethod?: string;
    roleName?: string;
    reason?: string;
    permissions?: string;
    rejectionReason?: string;
  };
}
export function Requests() {
  const navigate = useNavigate();
  const [merchantRequests] = useState<MerchantRequest[]>(() => {
    const now = new Date();
    // Pull any pending payout-account update submitted from Settings
    let payoutItems: MerchantRequest[] = [];
    try {
      const raw = sessionStorage.getItem('pendingPayoutUpdate');
      if (raw) {
        const p = JSON.parse(raw);
        payoutItems = [{
          id: p.id || 'PAYOUT-REQ',
          type: 'Bank Account Update',
          description: `Update payout bank account to ${p.bank} ${p.maskedAccount}`,
          requestedBy: 'Simon Lee',
          requestedAt: p.submittedAt || new Date().toISOString(),
          status: 'Pending',
          details: {
            reason: 'Merchant-initiated payout account change'
          }
        }];
      }
    } catch {
      // ignore parse errors
    }
    return [...payoutItems, {
      id: 'REQ-001',
      type: 'Payment Method Activation',
      description: 'Request to activate ShopeePay payment method',
      requestedBy: 'Simon Lee',
      requestedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Pending',
      details: {
        paymentMethod: 'ShopeePay',
        reason: 'Customer demand for ShopeePay payments'
      }
    }, {
      id: 'REQ-002',
      type: 'User Role Request',
      description: 'Request for custom "Customer Support" role',
      requestedBy: 'Sarah Tan',
      requestedAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        roleName: 'Customer Support',
        permissions: 'Read-only access to payments and customers'
      }
    }, {
      id: 'REQ-003',
      type: 'Payment Method Activation',
      description: 'Request to activate Boost eWallet',
      requestedBy: 'Ahmad Rahman',
      requestedAt: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        paymentMethod: 'Boost',
        reason: 'Expand payment options for customers',
        rejectionReason: 'Merchant category not supported by Boost'
      }
    }, {
      id: 'REQ-004',
      type: 'Payment Method Activation',
      description: 'Request to activate American Express',
      requestedBy: 'Simon Lee',
      requestedAt: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'In Progress',
      details: {
        paymentMethod: 'American Express',
        reason: 'Corporate clients prefer Amex'
      }
    }, {
      id: 'REQ-005',
      type: 'User Role Request',
      description: 'Request for "Marketing" role with limited access',
      requestedBy: 'Sarah Tan',
      requestedAt: new Date(now.getTime() - 12 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Pending',
      details: {
        roleName: 'Marketing',
        permissions: 'Read access to reports and customer data'
      }
    }, {
      id: 'REQ-006',
      type: 'Payment Method Activation',
      description: 'Request to activate DuitNow QR',
      requestedBy: 'Ahmad Rahman',
      requestedAt: new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        paymentMethod: 'DuitNow QR',
        reason: 'Low-cost payment option for customers'
      }
    }].sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime());
  });
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Closed':
        return 'bg-gray-100 text-gray-800';
      case 'Pending':
        return 'bg-amber-100 text-amber-800';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const then = new Date(timestamp);
    const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };
  return <div className="max-w-7xl mx-auto py-8 px-4" data-id="element-14629" data-mp="dto8rvah2ssw">
      <div className="mb-8" data-id="element-14630" data-mp="irg4z1i5h12w">
        <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-14631" data-mp="ishqjzwsl5yv">
          Merchant Requests
        </h1>
        <p className="text-gray-600" data-id="element-14632" data-mp="nphvu8lti4ox">View & track requests</p>
      </div>

      <div className="space-y-6" data-id="element-14633" data-mp="shrinfrmyomz">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-14634" data-mp="si19f5ki8ztq">
          <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-14635" data-mp="wjcq2lleiaps">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14636" data-mp="wl7g2ebj4t0q">
              Total Requests
            </p>
            <p className="text-2xl font-bold text-gray-900" data-id="element-14637" data-mp="16ghhp0y0mydz">
              {merchantRequests.length}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-14638" data-mp="1besmgx9geipc">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14639" data-mp="3895nni21nzxg">
              Pending
            </p>
            <p className="text-2xl font-bold text-amber-600" data-id="element-14640" data-mp="3d89w26h59tmq">
              {merchantRequests.filter(r => r.status === 'Pending').length}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-14641" data-mp="3087nv8u7xomo">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14642" data-mp="3f4yr5t70jkhk">
              Closed
            </p>
            <p className="text-2xl font-bold text-gray-600" data-id="element-14643" data-mp="3a5cfho6u2cav">
              {merchantRequests.filter(r => r.status === 'Closed').length}
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-14644" data-mp="3j6jzd18lralf">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-14645" data-mp="3j4ppqj2s74wn">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-14646" data-mp="1piv2y9dsgoj">
              <ClipboardListIcon size={18} className="text-gray-600" data-id="element-14647" data-mp="3sxohuaxhldl0" />
              All Requests ({merchantRequests.length})
            </h3>
          </div>
          <div className="overflow-x-auto" data-id="element-14648" data-mp="6pf3d9p42u9w">
            <table className="w-full" data-id="element-14649" data-mp="6qq5z66tlvzf">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-14650" data-mp="pdfs3fybrqny">
                <tr data-id="element-14651" data-mp="pe8phqbe93tf">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14652" data-mp="pbmhjpklcmao">
                    Request ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14653" data-mp="gc9cra7ewlwa">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14654" data-mp="6mpplsco25vm">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14655" data-mp="3trnq9txcrvnf">
                    Requested By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14656" data-mp="1o4pdsxezm76">
                    Requested At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14657" data-mp="3orj69tx8kisg">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-14658" data-mp="3769e9shut49">
                {merchantRequests.map(request => <tr key={request.id} onClick={() => navigate(`/requests/${request.id}`)} className="hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-14659" data-mp="3v82vpr74jmss">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 font-mono" data-id="element-14660" data-mp="3gkzupajlew0p">
                      {request.id}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900" data-id="element-14661" data-mp="3bmg9jcctpo9e">
                      {request.type}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs" data-id="element-14662" data-mp="37ii8ux7uo5e8">
                      {request.description}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900" data-id="element-14663" data-mp="2xn6u4ewfyy6h">
                      {request.requestedBy}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600" title={new Date(request.requestedAt).toLocaleString()} data-id="element-14664" data-mp="1d67eqm4e6fvw">
                      {getRelativeTime(request.requestedAt)}
                    </td>
                    <td className="px-6 py-4" data-id="element-14665" data-mp="1r2kyy30b8ve2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`} data-id="element-14666" data-mp="udlp4jk2vljr">
                        {request.status}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>;
}